import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SILENT_API_ERRORS } from '@data/_generated-api/http-interceptors/silent-api-errors';
import { PlatformFeedbackService } from '@data/_generated-api/platform-api/services/platform-feedback.service';
import { PlatformApiResultOfDataSourceResultDtoOfFeedbackSummaryDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-data-source-result-dto-of-feedback-summary-dto';
import { PlatformCreateFeedback } from '@data/_generated-api/platform-api/models/platform-create-feedback';
import { PlatformCreateFeedbackAttachment } from '@data/_generated-api/platform-api/models/platform-create-feedback-attachment';
import { PlatformFeedbackAttachmentContentDto } from '@data/_generated-api/platform-api/models/platform-feedback-attachment-content-dto';
import { PlatformFeedbackDetailDto } from '@data/_generated-api/platform-api/models/platform-feedback-detail-dto';
import { PlatformFeedbackStatus } from '@data/_generated-api/platform-api/models/platform-feedback-status';
import { PlatformFeedbackSummaryDto } from '@data/_generated-api/platform-api/models/platform-feedback-summary-dto';
import { SupportFeedbackQuery, buildSupportDataSourceQuery, supportUnreadParam } from './support-feedback-query';

/** Eén pagina van een meldingenoverzicht. */
export interface SupportFeedbackPage {
  items: PlatformFeedbackSummaryDto[];
  totalRecordCount: number;
  /** Pagina die de backend leverde: een te hoge pagina valt terug op de laatste. */
  page: number;
}

/**
 * Enige laag die de feedback-API kent (patroon HdChartGroupService).
 * Alle resultaten volgen het ApiResult-envelope-contract: isValid + value.
 */
@Injectable({ providedIn: 'root' })
export class SupportApiService {
  private readonly api = inject(PlatformFeedbackService);
  /**
   * Voor de ongelezen-tellers: die vertrekken vanzelf bij elke terugkeer naar de tab
   * (SupportBadgeService, visibilitychange), op gsm vaak nog vóór het netwerk terug is.
   * Zonder deze vlag toonde elke mislukte teller "Egitura Platform is offline!", terwijl
   * de gebruiker niets deed (docs/SESSIES.md).
   */
  private readonly silentContext = new HttpContext().set(SILENT_API_ERRORS, true);

  // ── Melder ────────────────────────────────────────────────────────────
  async create(cmd: PlatformCreateFeedback): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(this.api.postFeedbackActionCreate({ body: cmd }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De melding kon niet verzonden worden.');
    return result.value;
  }

  /** Eén pagina eigen meldingen: filters, zoekterm en pagina uit de overzichtstoestand. */
  async queryMine(query: SupportFeedbackQuery): Promise<SupportFeedbackPage> {
    const result = await firstValueFrom(this.api.postFeedbackMineQueryDatasource(this.listParams(query)));
    return this.toPage(result, query);
  }

  async getMine(id: string): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(this.api.getFeedbackMineId({ id }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De melding kon niet geladen worden.');
    return result.value;
  }

  async commentMine(id: string, body: string, language: string, attachments?: PlatformCreateFeedbackAttachment[]): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(
      this.api.postFeedbackMineActionComment({ body: { feedbackId: id, body, language, attachments: attachments ?? [] } }),
    );
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De reactie kon niet verstuurd worden.');
    return result.value;
  }

  async markSeenMine(id: string): Promise<void> {
    const result = await firstValueFrom(this.api.postFeedbackMineActionMarkSeen({ body: { feedbackId: id } }));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De melding kon niet als gelezen gemarkeerd worden.');
  }

  /** Alle eigen ongelezen meldingen als gelezen markeren zonder ze te openen; levert het aantal. */
  async markAllSeenMine(): Promise<number> {
    const result = await firstValueFrom(this.api.postFeedbackMineActionMarkAllSeen());
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De meldingen konden niet als gelezen gemarkeerd worden.');
    return result.value ?? 0;
  }

  async unreadMine(): Promise<number> {
    const result = await firstValueFrom(this.api.getFeedbackMineUnreadCount(undefined, this.silentContext));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'Het aantal ongelezen meldingen kon niet geladen worden.');
    return result.value?.count ?? 0;
  }

  async attachment(id: string): Promise<PlatformFeedbackAttachmentContentDto> {
    const result = await firstValueFrom(this.api.getFeedbackAttachmentsId({ id }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De bijlage kon niet geladen worden.');
    return result.value;
  }

  // ── Beheer ────────────────────────────────────────────────────────────
  /** Eén pagina van alle meldingen (beheer): filters, zoekterm en pagina uit de overzichtstoestand. */
  async queryAll(query: SupportFeedbackQuery): Promise<SupportFeedbackPage> {
    const result = await firstValueFrom(this.api.postFeedbackManageQueryDatasource(this.listParams(query)));
    return this.toPage(result, query);
  }

  async getManage(id: string): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(this.api.getFeedbackManageId({ id }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De melding kon niet geladen worden.');
    return result.value;
  }

  async commentManage(id: string, body: string, language: string, attachments?: PlatformCreateFeedbackAttachment[]): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(
      this.api.postFeedbackManageActionComment({ body: { feedbackId: id, body, language, attachments: attachments ?? [] } }),
    );
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De reactie kon niet verstuurd worden.');
    return result.value;
  }

  async setStatus(id: string, status: PlatformFeedbackStatus, comment: string | undefined, language: string): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(this.api.postFeedbackManageActionSetStatus({
      body: { feedbackId: id, status, comment: comment ?? null, language },
    }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De status kon niet gewijzigd worden.');
    return result.value;
  }

  /** Prioriteit (volgorde van uitvoering) zetten; null wist ze. Enkel voor beheerders. */
  async setPriority(id: string, priority: number | null): Promise<PlatformFeedbackDetailDto> {
    const result = await firstValueFrom(this.api.postFeedbackManageActionSetPriority({
      body: { feedbackId: id, priority },
    }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De prioriteit kon niet gewijzigd worden.');
    return result.value;
  }

  async markSeenManage(id: string): Promise<void> {
    const result = await firstValueFrom(this.api.postFeedbackManageActionMarkSeen({ body: { feedbackId: id } }));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De melding kon niet als gelezen gemarkeerd worden.');
  }

  async unreadManage(): Promise<number> {
    const result = await firstValueFrom(this.api.getFeedbackManageUnreadCount(undefined, this.silentContext));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'Het aantal ongelezen meldingen kon niet geladen worden.');
    return result.value?.count ?? 0;
  }

  // ── Overzichten ───────────────────────────────────────────────────────
  private listParams(query: SupportFeedbackQuery) {
    return {
      statuses: query.statuses,
      type: query.type,
      unread: supportUnreadParam(query.read),
      body: buildSupportDataSourceQuery(query),
    };
  }

  private toPage(result: PlatformApiResultOfDataSourceResultDtoOfFeedbackSummaryDto | undefined, query: SupportFeedbackQuery): SupportFeedbackPage {
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De meldingen konden niet geladen worden.');
    return {
      items: result.value?.items ?? [],
      totalRecordCount: result.value?.totalRecordCount ?? 0,
      page: result.value?.dataSourceQuery?.pageOption?.currentPageIndex ?? query.page,
    };
  }
}
